import React, { useState } from 'react';
import SubscriberCard from './SubscriberCard';
import './SubscriberCard.scss';

/**
 * Storybook/Usage Example for SubscriberCard
 * 
 * This demonstrates the SubscriberCard component in a horizontal row
 * matching the intended usage inside SubscriberSwiper/draggableTileGrid
 */
const SubscriberCardExample = () => {
  const [selectedId, setSelectedId] = useState('sub2');

  const subscribers = [
    {
      id: 'sub1',
      name: 'אריאלה',
      number: '054-56789034',
      hasService: false,
    },
    {
      id: 'sub2',
      name: 'אריאלה',
      number: '054-56789034',
      hasService: false,
    },
    {
      id: 'sub3',
      name: 'אריאלה',
      number: '054-56789034',
      hasService: true,
    },
    {
      id: 'sub4',
      name: 'דניאל',
      number: '050-12345678',
      hasService: false,
    },
    {
      id: 'sub5',
      name: 'יעל',
      number: '052-98765432',
      hasService: true,
    },
  ];

  const handleSelect = (id) => {
    console.log('Selected subscriber:', id);
    setSelectedId(id);
  };

  const handleAdd = (id) => {
    console.log('Add service for subscriber:', id);
    alert(`Adding service for subscriber ${id}`);
  };

  return (
    <div style={{ 
      padding: '40px 20px', 
      background: '#1a0a24',
      minHeight: '100vh'
    }}>
      <h2 style={{ color: '#fff', marginBottom: '20px', textAlign: 'right' }}>
        SubscriberCard Component Demo
      </h2>
      
      {/* Single row container matching SubscriberSwiper layout */}
      <div style={{ 
        display: 'flex',
        gap: '8px',
        overflowX: 'auto',
        padding: '10px 0',
        direction: 'rtl'
      }}>
        {subscribers.map((sub) => (
          <SubscriberCard
            key={sub.id}
            id={sub.id}
            name={sub.name}
            number={sub.number}
            hasService={sub.hasService}
            isSelected={sub.id === selectedId}
            onClick={handleSelect}
            onAdd={handleAdd}
          />
        ))}
      </div>

      <div style={{ marginTop: '30px', color: '#fff', textAlign: 'right' }}>
        <h3 style={{ marginBottom: '10px' }}>Current Selection: {selectedId}</h3>
        <div style={{ fontSize: '12px', color: '#e7e6e8', lineHeight: '1.6' }}>
          <p><strong>Interactions:</strong></p>
          <ul style={{ paddingRight: '20px' }}>
            <li>Click card to select</li>
            <li>Click + icon to add service (hasService: false)</li>
            <li>Music note icon is non-interactive (hasService: true)</li>
            <li>Keyboard: Tab to focus, Enter to select</li>
            <li>Keyboard: Focus + icon, Space/Enter to add</li>
            <li>Hover: card lifts slightly</li>
            <li>Selected: gradient background, pink name</li>
          </ul>
        </div>
      </div>

      {/* Individual state examples */}
      <div style={{ marginTop: '40px' }}>
        <h3 style={{ color: '#fff', marginBottom: '20px', textAlign: 'right' }}>
          Individual States
        </h3>
        <div style={{ 
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          direction: 'rtl'
        }}>
          <div>
            <p style={{ color: '#e7e6e8', fontSize: '12px', marginBottom: '8px' }}>
              Default (needs service)
            </p>
            <SubscriberCard
              id="demo1"
              name="אריאלה"
              number="054-56789034"
              hasService={false}
              isSelected={false}
              onClick={handleSelect}
              onAdd={handleAdd}
            />
          </div>

          <div>
            <p style={{ color: '#e7e6e8', fontSize: '12px', marginBottom: '8px' }}>
              Selected (needs service)
            </p>
            <SubscriberCard
              id="demo2"
              name="אריאלה"
              number="054-56789034"
              hasService={false}
              isSelected={true}
              onClick={handleSelect}
              onAdd={handleAdd}
            />
          </div>

          <div>
            <p style={{ color: '#e7e6e8', fontSize: '12px', marginBottom: '8px' }}>
              Selected (has service)
            </p>
            <SubscriberCard
              id="demo3"
              name="אריאלה"
              number="054-56789034"
              hasService={true}
              isSelected={true}
              onClick={handleSelect}
              onAdd={handleAdd}
            />
          </div>

          <div>
            <p style={{ color: '#e7e6e8', fontSize: '12px', marginBottom: '8px' }}>
              Default (has service)
            </p>
            <SubscriberCard
              id="demo4"
              name="דניאל"
              number="050-12345678"
              hasService={true}
              isSelected={false}
              onClick={handleSelect}
              onAdd={handleAdd}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriberCardExample;


/**
 * INTEGRATION EXAMPLE: SubscriberSwiper
 * 
 * import SubscriberCard from '../SubscriberCard/SubscriberCard';
 * import DraggableTileGrid from 'app/Components/Shared/DraggableTileGrid/DraggableTileGrid';
 * 
 * const SubscriberSwiper = ({ subscribers, selectedSubscriber, setSelectedSubscriber, onAdd }) => {
 *   return (
 *     <DraggableTileGrid
 *       items={subscribers}
 *       renderItem={(subscriber) => (
 *         <SubscriberCard
 *           id={subscriber.id}
 *           name={subscriber.name}
 *           number={subscriber.number}
 *           hasService={subscriber.hasService}
 *           isSelected={subscriber.id === selectedSubscriber}
 *           onClick={setSelectedSubscriber}
 *           onAdd={onAdd}
 *         />
 *       )}
 *       direction="rtl"
 *       gap={8}
 *     />
 *   );
 * };
 */


/**
 * TEST CASES (Jest + React Testing Library)
 * 
 * describe('SubscriberCard', () => {
 *   const mockProps = {
 *     id: 'sub1',
 *     name: 'אריאלה',
 *     number: '054-56789034',
 *     hasService: false,
 *     isSelected: false,
 *     onClick: jest.fn(),
 *     onAdd: jest.fn(),
 *   };
 * 
 *   test('renders subscriber name and number', () => {
 *     render(<SubscriberCard {...mockProps} />);
 *     expect(screen.getByText('אריאלה')).toBeInTheDocument();
 *     expect(screen.getByText('054-56789034')).toBeInTheDocument();
 *   });
 * 
 *   test('calls onClick when card is clicked', () => {
 *     render(<SubscriberCard {...mockProps} />);
 *     fireEvent.click(screen.getByRole('button'));
 *     expect(mockProps.onClick).toHaveBeenCalledWith('sub1');
 *   });
 * 
 *   test('calls onAdd when plus icon is clicked', () => {
 *     render(<SubscriberCard {...mockProps} />);
 *     const plusIcon = screen.getByLabelText('Add service');
 *     fireEvent.click(plusIcon);
 *     expect(mockProps.onAdd).toHaveBeenCalledWith('sub1');
 *   });
 * 
 *   test('does not call onAdd when music icon is clicked (hasService=true)', () => {
 *     render(<SubscriberCard {...mockProps} hasService={true} />);
 *     const musicIcon = screen.getByLabelText('Has service');
 *     fireEvent.click(musicIcon);
 *     expect(mockProps.onAdd).not.toHaveBeenCalled();
 *   });
 * 
 *   test('applies selected class when isSelected is true', () => {
 *     const { container } = render(<SubscriberCard {...mockProps} isSelected={true} />);
 *     expect(container.querySelector('.subscriber-card')).toHaveClass('selected');
 *   });
 * 
 *   test('supports keyboard activation with Enter', () => {
 *     render(<SubscriberCard {...mockProps} />);
 *     const card = screen.getByRole('button');
 *     fireEvent.keyDown(card, { key: 'Enter' });
 *     expect(mockProps.onClick).toHaveBeenCalledWith('sub1');
 *   });
 * 
 *   test('supports keyboard activation of plus icon with Space', () => {
 *     render(<SubscriberCard {...mockProps} />);
 *     const plusIcon = screen.getByLabelText('Add service');
 *     fireEvent.keyDown(plusIcon, { key: ' ' });
 *     expect(mockProps.onAdd).toHaveBeenCalledWith('sub1');
 *   });
 * 
 *   test('renders correct icon based on hasService prop', () => {
 *     const { rerender } = render(<SubscriberCard {...mockProps} hasService={false} />);
 *     expect(screen.getByAltText('Add')).toBeInTheDocument();
 *     
 *     rerender(<SubscriberCard {...mockProps} hasService={true} />);
 *     expect(screen.getByAltText('Music note')).toBeInTheDocument();
 *   });
 * });
 */
